import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import Logo from '../img/logo.png';

export interface TelmeSEOProps {
  title: string;
  subtitle?: string;
  description?: string;
  type?: string;
  card?: string;
  image?: string;
  noIndex?: boolean;
  sku?: string;
}

const BASE_URL = 'https://telme.com.ar';

const appendBrandName = (text: string, brandName: string) =>
  text.includes(brandName) ? text : `${text} | ${brandName}`;

const toAbsoluteUrl = (path: string) =>
  path.startsWith('http') ? path : `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;

const TelmeSEO = ({
  title,
  description,
  type,
  subtitle,
  card,
  image,
  noIndex,
  sku,
}: TelmeSEOProps) => {
  const { t } = useTranslation();
  const brandName = t('brandName');
  const currentUrl = window.location.href;
  const metaDescription = description || t('shared.phrase');
  const absoluteImage = toAbsoluteUrl(image || Logo);
  const fullTitle = appendBrandName(title, brandName);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={currentUrl} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}
      {/* Open Graph */}
      <meta property="og:type" content={type || 'website'} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:site_name" content={brandName} />
      <meta property="og:image" content={absoluteImage} />
      <meta property="og:image:alt" content={brandName} />
      <meta property="og:locale" content="es_AR" />
      {/* JSON-LD */}
      {type === 'product' && sku && (
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: fullTitle,
            description: metaDescription,
            sku,
            brand: { '@type': 'Brand', name: 'Industrias Telme' },
            image: absoluteImage,
            url: currentUrl,
          })}
        </script>
      )}
      {/* Twitter */}
      <meta name="twitter:card" content={card || 'summary_large_image'} />
      <meta name="twitter:title" content={appendBrandName(subtitle || title, brandName)} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={absoluteImage} />
    </Helmet>
  );
};

export default TelmeSEO;
